/**
 * localStorage Compression Utilities
 * Reduziert Speichernutzung durch Komprimierung großer Datenstrukturen
 */

import LZString from './lzString.js';
import { kvSet, kvGet, kvRemove, listKvKeys } from '../infrastructure/storage/kvAccess';

// Optimierte Komprimierungs-Konfiguration
const COMPRESSION_CONFIG = {
  MIN_SIZE_FOR_COMPRESSION: 5000,  // Erhöht auf 5KB für bessere Effizienz
  COMPRESSION_THRESHOLD: 0.5,      // Komprimierung nur wenn > 50% Reduktion
  MAX_COMPRESSION_RATIO: 0.8,      // Entferne Komprimierung wenn > 80% des Originals
  COMPRESSIBLE_KEYS: [             // Nur neue Struktur komprimieren
    'Elkheir:v1:biz:',
    'business_',
    'products',
    'sales',
    'purchases',
    'customers',
    'lots',
    'reports'
  ],
  EXCLUDE_KEYS: [                  // Keys die NICHT komprimiert werden sollen
    'currentUser',
    'currentBusiness',
    'migration_',
    'backup_',
    'temp_',
    'legacy_',
    'worker_sessions_',
    'sequences_',
    'newCashRegisterData_',
    'cashRegisterData_'
  ]
};

/** Schlüssel, die durch den Auto-Save / KV-Stack optional als Kompressions-Envelope gespeichert werden. */
export function shouldCompressKvKey(key) {
  if (key == null || key === '') return false;
  const k = String(key);
  if (COMPRESSION_CONFIG.EXCLUDE_KEYS.some((ex) => k.includes(ex))) return false;
  return COMPRESSION_CONFIG.COMPRESSIBLE_KEYS.some((c) => k.includes(c));
}

/**
 * Wert aus Engine.get / JSON.parse: falls Kompressions-Envelope, inneres Business-Objekt zurück.
 */
export function unwrapCompressionEnvelopeValue(key, parsed) {
  if (parsed == null || typeof parsed !== 'object') return parsed;
  if (
    typeof parsed.data === 'string' &&
    typeof parsed.compressed === 'boolean' &&
    typeof parsed.originalSize === 'number'
  ) {
    try {
      const inner = decompressData(key, parsed.data, parsed.compressed);
      if (inner != null) return inner;
    } catch (e) {
      console.warn('[compression] unwrap failed', key, e);
    }
  }
  return parsed;
}

/**
 * Rohstring von getRaw: falls Envelope, inneres Objekt als JSON-String (wie früher putRaw).
 */
export function unwrapEnvelopeRawStringToInnerJsonString(key, rawString) {
  if (rawString == null || rawString === '') return rawString;
  let parsed;
  try {
    parsed = JSON.parse(rawString);
  } catch {
    return rawString;
  }
  const inner = unwrapCompressionEnvelopeValue(key, parsed);
  if (inner === parsed) return rawString;
  return JSON.stringify(inner);
}

/**
 * Komprimiert Daten wenn es sinnvoll ist
 */
export const compressData = (key, data) => {
  try {
    const jsonString = JSON.stringify(data);
    const originalSize = jsonString.length;
    
    // Prüfe ob Komprimierung sinnvoll ist
    if (originalSize < COMPRESSION_CONFIG.MIN_SIZE_FOR_COMPRESSION) {
      return {
        compressed: false,
        data: jsonString,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1
      };
    }
    
    // Prüfe ob Key komprimierbar ist
    const isCompressible = COMPRESSION_CONFIG.COMPRESSIBLE_KEYS.some(compressibleKey => 
      key.includes(compressibleKey)
    );
    
    // Prüfe ob Key ausgeschlossen ist
    const isExcluded = COMPRESSION_CONFIG.EXCLUDE_KEYS.some(excludeKey => 
      key.includes(excludeKey)
    );
    
    if (!isCompressible || isExcluded) {
      return {
        compressed: false,
        data: jsonString,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1
      };
    }
    
    // Versuche Komprimierung
    const compressed = LZString.compress(jsonString);
    const compressedSize = compressed.length;
    const ratio = compressedSize / originalSize;
    
    // Verwende Komprimierung nur wenn sie effektiv ist
    if (ratio < COMPRESSION_CONFIG.COMPRESSION_THRESHOLD) {
      return {
        compressed: true,
        data: compressed,
        originalSize,
        compressedSize,
        compressionRatio: ratio
      };
    }
    
    return {
      compressed: false,
      data: jsonString,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1
    };
    
  } catch (error) {
    console.error('Error compressing data:', error);
    return {
      compressed: false,
      data: JSON.stringify(data),
      originalSize: 0,
      compressedSize: 0,
      compressionRatio: 1,
      error: error.message
    };
  }
};

/**
 * Dekomprimiert Daten
 */
export const decompressData = (key, data, compressed = false) => {
  try {
    if (!compressed) {
      return JSON.parse(data);
    }
    
    const decompressed = LZString.decompress(data);
    return JSON.parse(decompressed);
    
  } catch (error) {
    console.error('Error decompressing data:', error);
    // Fallback: Versuche als normale JSON zu parsen
    try {
      return JSON.parse(data);
    } catch (fallbackError) {
      console.error('Fallback parsing also failed:', fallbackError);
      return null;
    }
  }
};

/**
 * Erweiterte localStorage-Operationen mit Komprimierung
 */
export const compressedLocalStorage = {
  /**
   * Setzt Item mit automatischer Komprimierung
   */
  setItem: (key, value) => {
    try {
      const compressionResult = compressData(key, value);
      
      // Speichere mit Metadaten
      const storageData = {
        data: compressionResult.data,
        compressed: compressionResult.compressed,
        originalSize: compressionResult.originalSize,
        compressedSize: compressionResult.compressedSize,
        compressionRatio: compressionResult.compressionRatio,
        timestamp: Date.now()
      };
      
      kvSet(key, storageData);
      
      // Logging für Monitoring
      if (compressionResult.compressed) {
        console.log(`🗜️ [COMPRESSION] ${key}: ${compressionResult.originalSize} → ${compressionResult.compressedSize} bytes (${(compressionResult.compressionRatio * 100).toFixed(1)}%)`);
      }
      
      return {
        success: true,
        compressed: compressionResult.compressed,
        savings: compressionResult.originalSize - compressionResult.compressedSize,
        compressionRatio: compressionResult.compressionRatio
      };
      
    } catch (error) {
      console.error('Error in compressed setItem:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  /**
   * Holt Item mit automatischer Dekomprimierung
   */
  getItem: (key) => {
    try {
      const parsed = kvGet(key);
      if (parsed == null) return null;

      if (typeof parsed === 'object' && parsed !== null && parsed.compressed !== undefined) {
        return decompressData(key, parsed.data, parsed.compressed);
      }

      return parsed;
      
    } catch (error) {
      console.error('Error in compressed getItem:', error);
      return null;
    }
  },
  
  /**
   * Entfernt Item
   */
  removeItem: (key) => {
    try {
      kvRemove(key);
      return { success: true };
    } catch (error) {
      console.error('Error in compressed removeItem:', error);
      return { success: false, error: error.message };
    }
  },
  
  /**
   * Holt Komprimierungs-Statistiken
   */
  getCompressionStats: () => {
    const stats = {
      totalKeys: 0,
      compressedKeys: 0,
      totalOriginalSize: 0,
      totalCompressedSize: 0,
      totalSavings: 0,
      averageCompressionRatio: 0
    };
    
    try {
      listKvKeys().forEach((key) => {
        const parsed = kvGet(key);
        if (parsed == null || typeof parsed !== 'object') return;

        if (parsed.compressed !== undefined) {
          stats.totalKeys++;
          stats.totalOriginalSize += parsed.originalSize || 0;
          stats.totalCompressedSize += parsed.compressedSize || 0;

          if (parsed.compressed) {
            stats.compressedKeys++;
          }
        }
      });
      stats.totalSavings = stats.totalOriginalSize - stats.totalCompressedSize;
      stats.averageCompressionRatio = stats.totalOriginalSize > 0 ? 
        stats.totalCompressedSize / stats.totalOriginalSize : 1;
      
    } catch (error) {
      console.error('Error calculating compression stats:', error);
    }
    
    return stats;
  }
};

export default compressedLocalStorage;